/**
 * URQL GraphQL Client Factory
 * ---------------------------------
 * WHY:
 * Provides a shared URQL client instance for Magento GraphQL API.
 * Prevents re-creating the client on every request.
 */

import type { Client } from '@urql/core'
import { createClient, fetchExchange } from '@urql/core'

let client: Client | null = null
/**
 * Returns a singleton GraphQL client instance
 */
let lastPassAuthHeader: boolean | null = null

export function getGraphqlClient({ passAuthHeader = true }): Client {
    const runtimeConfig = useRuntimeConfig() as { public?: { graphqlApiUrl?: string } }

    const vueAppMagentoGraphqlUrl = runtimeConfig.public?.graphqlApiUrl

    const { authHeader } = useAuthCustomerAccs()

    if (!client || lastPassAuthHeader !== passAuthHeader) {
        lastPassAuthHeader = passAuthHeader

        client = createClient({
            url: vueAppMagentoGraphqlUrl as string,
            exchanges: [fetchExchange],
            requestPolicy: 'network-only',
            /**
             * Dynamically attach headers before each request
             */
            fetchOptions: () => {
                const headers: Record<string, string> = {
                    'Content-Type': 'application/json',

                    'Magento-Store-View-Code': 'default',
                    'Magento-Website-Code': 'base',
                    'Magento-Store-Code': 'default',
                }

                // eslint-disable-next-line no-useless-catch
                try {
                    const auth = authHeader?.()

                    if (passAuthHeader) {
                        if (typeof auth === 'string' && auth) {
                            headers['Authorization'] = auth
                        } else if (auth && typeof auth === 'object') {
                            Object.assign(headers, auth)
                        }
                    }
                } catch (e) {
                    throw e
                }

                return {
                    headers, // Disable introspection to avoid Federation conflicts
                    introspection: false,
                }
            },
        })
    }

    return client
}
